import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Video, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AppointmentCard } from "@/components/livecoach/AppointmentCard";
import { RespondDialog, ThreadDialog } from "@/components/livecoach/LiveCoachDialogs";
import { fmtTime } from "@/lib/livecoach";

export default function PlayerLiveCoach() {
  const { user } = useAuth();
  const [appts, setAppts] = useState(null);
  const [coaches, setCoaches] = useState([]);
  const [respondTo, setRespondTo] = useState(null);
  const [threadFor, setThreadFor] = useState(null);
  const [booking, setBooking] = useState(null); // coach being booked

  const load = useCallback(() => {
    api.get("/livecoach/appointments/me").then((r) => setAppts(r.data.appointments)).catch((e) => toast.error(formatApiError(e)));
    api.get("/livecoach/coaches").then((r) => setCoaches(r.data.coaches)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancel = async (a) => {
    if (!window.confirm("Cancel this session?")) return;
    try { await api.post(`/livecoach/appointments/${a.appt_id}/cancel`); toast.success("Cancelled"); load(); }
    catch (er) { toast.error(formatApiError(er)); }
  };

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: "var(--ink-1)" }} data-testid="player-livecoach-page">
      <div className="max-w-2xl mx-auto px-5 pt-8">
        <Link to="/portal" className="text-xs font-bold text-white/50 hover:text-white flex items-center gap-1.5"><ArrowLeft className="h-3.5 w-3.5" /> Back to portal</Link>
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-[0.25em] font-black text-emerald-400 flex items-center gap-1.5"><Video className="h-3.5 w-3.5" /> BioHacker Live Coach™</div>
          <h1 className="text-3xl font-black text-white mt-1">Train live with your coach</h1>
          <p className="text-sm text-white/50 mt-1">Book a private live video session — see it live, correct it live.</p>
        </div>

        <h2 className="text-sm font-black text-white/70 uppercase tracking-wider mt-8 mb-3">Book a coach</h2>
        <div className="space-y-3">
          {coaches.length === 0 && <div className="text-xs text-white/40">No bookable coaches on your team yet.</div>}
          {coaches.map((c) => (
            <div key={c.user_id} className="rounded-xl border border-[var(--ink-border)] p-4 flex items-center justify-between gap-3" style={{ backgroundColor: "var(--ink-2)" }} data-testid={`lc-coach-${c.user_id}`}>
              <div>
                <div className="font-bold text-white">{c.name}</div>
                <div className="text-[11px] text-white/45">
                  {c.has_availability ? `${c.slot_minutes}-min slots${c.instant_booking ? " · instant booking" : " · approval required"}` : "No open slots listed — send a request"}
                </div>
              </div>
              <Button size="sm" onClick={() => setBooking(c)} className="bg-emerald-500 hover:bg-emerald-400 text-[#04121f] font-black" data-testid={`lc-book-${c.user_id}`}>Book</Button>
            </div>
          ))}
        </div>

        <h2 className="text-sm font-black text-white/70 uppercase tracking-wider mt-8 mb-3">My sessions</h2>
        <div className="space-y-3" data-testid="lc-player-sessions">
          {appts === null ? <div className="text-white/40 animate-pulse text-sm">Loading…</div> :
            appts.length === 0 ? <div className="text-white/40 text-sm rounded-xl border border-dashed border-white/15 p-6 text-center"><CalendarClock className="h-5 w-5 mx-auto mb-2 opacity-50" />No live sessions yet.</div> :
              appts.map((a) => <AppointmentCard key={a.appt_id} a={a} meId={user?.user_id} onRespond={setRespondTo} onCancel={cancel} onThread={setThreadFor} />)}
        </div>
      </div>

      {respondTo && <RespondDialog appt={respondTo} onClose={() => setRespondTo(null)} onDone={load} />}
      {threadFor && <ThreadDialog appt={threadFor} onClose={() => setThreadFor(null)} />}
      {booking && <BookSheet coach={booking} onClose={() => setBooking(null)} onDone={load} />}
    </div>
  );
}

const BookSheet = ({ coach, onClose, onDone }) => {
  const [date, setDate] = useState(new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [slots, setSlots] = useState(null);
  const [pick, setPick] = useState("");
  const [custom, setCustom] = useState("");
  const [objective, setObjective] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setSlots(null); setPick("");
    api.get(`/livecoach/coaches/${coach.user_id}/slots?date=${date}`)
      .then((r) => setSlots(r.data.slots))
      .catch(() => setSlots([]));
  }, [date, coach.user_id]);

  const submit = async () => {
    const time = pick || (custom ? new Date(custom).toISOString() : "");
    if (!objective.trim() || !time) return toast.error("Add an objective and pick a time");
    setBusy(true);
    try {
      const { data } = await api.post("/livecoach/appointments", {
        coach_user_id: coach.user_id, objective: objective.trim(), start_at: time,
        duration_minutes: coach.slot_minutes || 30,
      });
      toast.success(data.appointment.status === "confirmed" ? "Session confirmed!" : "Request sent — your coach will respond");
      onDone(); onClose();
    } catch (er) { toast.error(formatApiError(er)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose}>
      <div className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border border-white/10 p-5 space-y-4" style={{ backgroundColor: "var(--ink-1)" }} onClick={(e) => e.stopPropagation()} data-testid="lc-book-sheet">
        <h3 className="font-black text-white text-lg">Book {coach.name}</h3>
        <Textarea placeholder="What do you want to work on? *" value={objective} onChange={(e) => setObjective(e.target.value)} className="bg-white/5 border-white/15 text-white" data-testid="lc-book-objective" />
        <label className="block text-xs space-y-1"><span className="text-white/50 font-bold">Date</span>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-white/5 border-white/15 text-white" data-testid="lc-book-date" />
        </label>
        <div>
          <div className="text-xs font-bold text-white/50 mb-1.5">Open slots {coach.timezone ? `(shown in your local time)` : ""}</div>
          {slots === null ? <div className="text-xs text-white/40 animate-pulse">Loading slots…</div> :
            slots.length === 0 ? <div className="text-xs text-white/40">No open slots this day — propose a time below instead.</div> : (
              <div className="flex flex-wrap gap-2" data-testid="lc-book-slots">
                {slots.map((s) => (
                  <button key={s} onClick={() => { setPick(s); setCustom(""); }} className={`px-3 py-1.5 rounded-lg text-xs font-bold border ${pick === s ? "bg-emerald-500/20 border-emerald-400/60 text-emerald-200" : "bg-white/5 border-white/10 text-white/70"}`}>
                    {fmtTime(s).split(", ").slice(-1)[0]}
                  </button>
                ))}
              </div>
            )}
        </div>
        <label className="block text-xs space-y-1"><span className="text-white/50 font-bold">Or propose your own time</span>
          <Input type="datetime-local" value={custom} onChange={(e) => { setCustom(e.target.value); setPick(""); }} className="bg-white/5 border-white/15 text-white" data-testid="lc-book-custom-time" />
        </label>
        <Button onClick={submit} disabled={busy} className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#04121f] font-black py-5" data-testid="lc-book-submit">
          {busy ? "Booking…" : coach.instant_booking && pick ? "Confirm session" : "Send request"}
        </Button>
      </div>
    </div>
  );
};
